import React, { useEffect, ReactNode, useCallback, useState, useRef, useLayoutEffect } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import {
  BackdropImageType,
  CircleStairsType,
  CircleSymbolType,
  DoorType,
  DoubleDoorType,
  SlidingDoorType,
  isAreaSymbolType,
  isAreaFillSymbolType,
  isCircleStairsType,
  isCircleSymbolType,
  isPolygonSymbolType,
  isDoorType,
  isSlidingDoorType,
  isDoubleDoorType,
  isRectStairsType,
  isTurnStairsType,
  isSquareSymbolType,
  isSmartTableType,
  isBookShelfType,
  isSvgType,
  isTextType,
  isTriangleSymbolType,
  isWindowType,
  isVoidType,
  isBackdropImageType,
  RectStairsType,
  TurnStairsType,
  SquareSymbolType,
  PolygonSymbolType,
  SvgType,
  SmartTableType,
  BookShelfType,
  TextType,
  TriangleSymbolType,
  VoidType,
  WindowType,
} from "../../types/floorplannerTypes";
import Grid from "../FloorPlan/Grid";
import WallStructure from "../FloorPlan/WallStructure";
import { editorStoreContext } from "../../store/editorStore";
import Door from "../FloorPlan/Door";
import SlidingDoor from "../FloorPlan/SlidingDoor";
import Window from "../FloorPlan/Window";
import { FloorplannerStoreContext, floorplannerStore } from "../../store/floorplannerStore";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import DoubleDoor from "../FloorPlan/DoubleDoor";
import CircleStairs from "../FloorPlan/CircleStairs";
import RectStairs from "../FloorPlan/RectStairs";
import TurnStairs from "../FloorPlan/TurnStairs";
import SvgSymbol from "../FloorPlan/SvgSymbol";
import SmartTableSymbol from "../FloorPlan/SmartTableSymbol";
import BookShelfSymbol from "../FloorPlan/BookShelfSymbol";
import TextSymbol from "../FloorPlan/TextSymbol";
import CameraSetup from "../FloorPlan/CameraSetup";
import FloorPlan2DCamera from "../FloorPlan/FloorPlan2DCamera";
import PanningPlane from "./PanningPlane";
import {
  TWO_D_CAMERA_Z,
  createFloorPlan2DOrthographicCamera,
} from "../../utils/floorPlan2DCamera";
import WallConstructionPlane from "./WallConstructionPlane";
import SelectionPlane from "./SelectionPlane";
import SquareSymbol from "../FloorPlan/SquareSymbol";
import CircleSymbol from "../FloorPlan/CircleSymbol";
import TriangleSymbol from "../FloorPlan/TriangleSymbol";
import PolygonSymbol from "../FloorPlan/PolygonSymbol";
import SingleLinesStructure from "../FloorPlan/SingleLinesStructure";
import LineConstructionPlane from "./LineConstructionPlane";
import RulerConstructionPlane from "./RulerConstructionPlane";
import AreaConstructionPlane from "./AreaConstructionPlane";
import AreaBleedFillPlane from "./AreaBleedFillPlane";
// AlignmentLines import not used directly; structure component is used below
import AlignmentLineStructure from "../FloorPlan/AlignmentLineStructure";
import RoomLabelStructure from "../FloorPlan/RoomLabelStructure";
import AreaSymbol from "../FloorPlan/AreaSymbol";
import { renderStore } from "../../store/renderStore";
import performanceMonitor from "../../utils/PerformanceMonitor";
import RulerLines from "../FloorPlan/RulerLines";
import { BackdropImage } from "../FloorPlan/BackdropImage";
import Void from "../FloorPlan/Void";
import JointPolygonStructure from "../FloorPlan/JointPolygonStructure";
import WallOutlineStructure from "../FloorPlan/WallOutlineStructure";
import DebugVisualization from "../FloorPlan/DebugVisualization";
import { wallGeometryCache } from "../../utils/WallGeometryCache";
import FloorPlan3DScene from "../../features/floorplan3d/scene/FloorPlan3DScene";
import { canRevealThreeDContent, copyCanvasFrame, releaseCanvasFrame } from "../../utils/canvasViewTransition";

const CONNECTION_METADATA_PANEL_WIDTH = 320;
const CONNECTION_METADATA_PANEL_MIN_HEIGHT = 180;
const CONNECTION_METADATA_PANEL_MAX_HEIGHT = 520;
const DEBUG_PANEL_VIEWPORT_MARGIN = 16;
const THREE_D_LOADING_INDICATOR_DELAY_MS = 700;
const THREE_D_LOADING_INDICATOR_MIN_VISIBLE_MS = 350;
const VIEW_TRANSITION_FADE_MS = 260;
const THREE_D_READY_SETTLE_DELAY_MS = VIEW_TRANSITION_FADE_MS + 40;

type ViewTransitionPhase = "idle" | "holding" | "fading";

const capturePerformanceSnapshot = (): {
  metrics: any;
  alerts: any[];
  analysis: any;
  bottlenecks: string[];
  dragMetrics: any;
} => ({
  metrics: performanceMonitor.getMetrics(),
  alerts: performanceMonitor.getAlerts(),
  analysis: performanceMonitor.analyzePerformance(),
  bottlenecks: performanceMonitor.detectBottlenecks(),
  dragMetrics: performanceMonitor.getDragPerformanceMetrics(),
});


interface StoreProviderProps {
  children: ReactNode;
}

export const FloorplannerStoreProvider: React.FC<StoreProviderProps> = ({
  children,
}) => {
  const { scene } = useThree();

  useEffect(() => {
    if (scene && scene.children.length > 0) {
      const box = new THREE.Box3();
      scene.children.forEach((child) => {
        if (child && child instanceof THREE.Mesh && child.geometry) {
          // Check if geometry has valid bounding box
          try {
            // Try to compute bounding box to catch NaN values early
            if (!child.geometry.boundingBox) {
              child.geometry.computeBoundingBox();
            }

            // Skip if bounding box contains NaN or infinite values
            const bbox = child.geometry.boundingBox;
            if (!bbox ||
              !Number.isFinite(bbox.min.x) || !Number.isFinite(bbox.min.y) || !Number.isFinite(bbox.min.z) ||
              !Number.isFinite(bbox.max.x) || !Number.isFinite(bbox.max.y) || !Number.isFinite(bbox.max.z)) {
              console.warn('Skipping object with invalid bounding box:', child);
              return;
            }

            // Additional check for degenerate bounding boxes (all points at same location)
            if (bbox.min.x === bbox.max.x && bbox.min.y === bbox.max.y && bbox.min.z === bbox.max.z) {
              return;
            }

            box.expandByObject(child);
          } catch (error) {
            console.warn('Error computing bounding box for object:', child, error);
            return;
          }
        }
      });
      floorplannerStore.boundingBox = box;
      floorplannerStore.scene = scene;
      renderStore.scene = scene;
    }
  }, [scene.children]);

  return (
    <FloorplannerStoreContext.Provider value={floorplannerStore}>
      {children}
    </FloorplannerStoreContext.Provider>
  );
};

const TopDown2DCameraGuard: React.FC<{
  restoredView: CameraViewSnapshot | null;
}> = observer(({ restoredView }) => {
  const editorStore = React.useContext(editorStoreContext);
  const { camera, invalidate } = useThree();
  // Only re-apply a snapshot when exiting 3D (restoredView changes) or the
  // active camera instance changes — never on every pan/zoom store update.
  const restoredViewKey = restoredView
    ? `${restoredView.x}:${restoredView.y}:${restoredView.zoom}`
    : "none";

  useLayoutEffect(() => {
    if (!(camera instanceof THREE.PerspectiveCamera || camera instanceof THREE.OrthographicCamera)) return;

    if (editorStore.view3D) {
      camera.updateMatrixWorld();
      editorStore.setCamera(camera);
      editorStore.remember2DCameraView(camera);
      return;
    }

    const view = restoredView ?? editorStore.last2DCameraView;
    // Orthographic 2D ignores Z for scale, but keep the legacy height so
    // 2D↔3D entry distance math stays consistent.
    camera.position.set(view.x, view.y, TWO_D_CAMERA_Z);
    camera.zoom = view.zoom;
    camera.up.set(0, 1, 0);
    camera.lookAt(camera.position.x, camera.position.y, 0);
    camera.updateMatrixWorld();
    camera.updateProjectionMatrix();
    editorStore.setCamera(camera);
    editorStore.remember2DCameraView(camera);
    invalidate();
    // Intentionally omit last2DCameraView — pan owns the live camera position.
  }, [camera, editorStore, editorStore.view3D, invalidate, restoredView, restoredViewKey]);

  return null;
});

type CameraViewSnapshot = {
  x: number;
  y: number;
  z: number;
  zoom: number;
};

const View3DSceneMountBridge: React.FC<{
  show3DScene: boolean;
  setShow3DScene: React.Dispatch<React.SetStateAction<boolean>>;
  setEntry2DView: React.Dispatch<React.SetStateAction<CameraViewSnapshot | null>>;
  onBeforeEnter3D: (canvas: HTMLCanvasElement) => void;
}> = observer(({ show3DScene, setShow3DScene, setEntry2DView, onBeforeEnter3D }) => {
  const editorStore = React.useContext(editorStoreContext);
  const { camera, gl } = useThree();

  useLayoutEffect(() => {
    if (!editorStore.view3D || show3DScene) return;
    onBeforeEnter3D(gl.domElement);
    if (!(camera instanceof THREE.PerspectiveCamera || camera instanceof THREE.OrthographicCamera)) {
      setShow3DScene(true);
      return;
    }

    camera.updateMatrixWorld();
    const entryView = {
      x: camera.position.x,
      y: camera.position.y,
      z: TWO_D_CAMERA_Z,
      zoom: camera.zoom,
    };
    editorStore.setCamera(camera);
    editorStore.remember2DCameraView(camera);
    setEntry2DView(entryView);
    setShow3DScene(true);
  }, [camera, editorStore, editorStore.view3D, gl.domElement, onBeforeEnter3D, setEntry2DView, setShow3DScene, show3DScene]);

  return null;
});

const FloorPlan: React.FC = observer(() => {
  const editorStore = React.useContext(editorStoreContext);
  const { saveToCloud, symbolsMap } = React.useContext(FloorplannerStoreContext);

  // Debug panel drag state - positioned directly under Building blocks menu on left side
  // LeftPanel is w-40 (160px) at left-3 (12px), so it ends at ~172px. Position debug window to align with sidebar left edge
  const [debugPanelPosition, setDebugPanelPosition] = useState({ x: 12, y: 320 }); // Aligned with sidebar left edge, below Building blocks menu
  const [isDraggingDebugPanel, setIsDraggingDebugPanel] = useState(false);
  const debugPanelRef = useRef<HTMLDivElement>(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const [performanceSnapshot, setPerformanceSnapshot] = useState(() => capturePerformanceSnapshot());
  /** Stable orthographic camera so 2D has no perspective parallax. */
  const [floorPlan2DCamera] = useState(() => createFloorPlan2DOrthographicCamera());
  const [show3DScene, setShow3DScene] = useState(editorStore.view3D);
  const [entry2DView, setEntry2DView] = useState<CameraViewSnapshot | null>(null);
  const [threeDModelsLoading, setThreeDModelsLoading] = useState(false);
  const [showThreeDModelsLoading, setShowThreeDModelsLoading] = useState(false);
  const threeDLoadingIndicatorShownAtRef = useRef<number | null>(null);
  const transitionCanvasRef = useRef<HTMLCanvasElement>(null);
  const [viewTransitionPhase, setViewTransitionPhase] = useState<ViewTransitionPhase>("idle");
  const [threeDBaseFrameReady, setThreeDBaseFrameReady] = useState(editorStore.view3D);
  const [showThreeDMeasurements, setShowThreeDMeasurements] = useState(editorStore.view3D);

  const finishViewTransition = useCallback(() => {
    setViewTransitionPhase("idle");
    if (transitionCanvasRef.current) {
      releaseCanvasFrame(transitionCanvasRef.current);
    }
  }, []);

  const capture2DFrameForTransition = useCallback((canvas: HTMLCanvasElement) => {
    setThreeDBaseFrameReady(false);
    setShowThreeDMeasurements(false);
    const transitionCanvas = transitionCanvasRef.current;
    if (!transitionCanvas) return;

    if (copyCanvasFrame(canvas, transitionCanvas)) {
      setViewTransitionPhase("holding");
    } else {
      finishViewTransition();
    }
  }, [finishViewTransition]);

  const reveal3DFrame = useCallback(() => {
    setViewTransitionPhase((phase) => phase === "holding" ? "fading" : phase);
  }, []);

  const handleFirst3DFrameReady = useCallback(() => {
    setThreeDBaseFrameReady(true);
  }, []);

  useEffect(() => {
    if (!canRevealThreeDContent({
      active: editorStore.view3D,
      sceneMounted: show3DScene,
      firstFrameReady: threeDBaseFrameReady,
      modelsLoading: threeDModelsLoading,
    })) return;

    const timeoutId = window.setTimeout(() => {
      reveal3DFrame();
      setShowThreeDMeasurements(true);
    }, THREE_D_READY_SETTLE_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [editorStore.view3D, reveal3DFrame, show3DScene, threeDBaseFrameReady, threeDModelsLoading]);

  useEffect(() => {
    if (viewTransitionPhase !== "fading") return;

    const timeoutId = window.setTimeout(() => {
      finishViewTransition();
    }, VIEW_TRANSITION_FADE_MS + 100);

    return () => window.clearTimeout(timeoutId);
  }, [finishViewTransition, viewTransitionPhase]);

  useEffect(() => {
    const shownAt = threeDLoadingIndicatorShownAtRef.current;
    const timeoutId = window.setTimeout(() => {
      if (threeDModelsLoading) {
        threeDLoadingIndicatorShownAtRef.current = performance.now();
        setShowThreeDModelsLoading(true);
        return;
      }

      threeDLoadingIndicatorShownAtRef.current = null;
      setShowThreeDModelsLoading(false);
    }, threeDModelsLoading
      ? THREE_D_LOADING_INDICATOR_DELAY_MS
      : Math.max(
          0,
          THREE_D_LOADING_INDICATOR_MIN_VISIBLE_MS -
            (shownAt == null ? THREE_D_LOADING_INDICATOR_MIN_VISIBLE_MS : performance.now() - shownAt),
        ));
    return () => window.clearTimeout(timeoutId);
  }, [threeDModelsLoading]);

  // Connection Metadata panel drag state
  const [connectionMetadataPosition, setConnectionMetadataPosition] = useState({ x: window.innerWidth - 340, y: 320 });
  const [isDraggingConnectionMetadata, setIsDraggingConnectionMetadata] = useState(false);
  const connectionMetadataRef = useRef<HTMLDivElement>(null);
  const connectionMetadataDragStart = useRef({ x: 0, y: 0 });

  // Performance Metrics panel drag state
  const [performancePanelPosition, setPerformancePanelPosition] = useState({ x: window.innerWidth - 340, y: 560 });
  const [isDraggingPerformancePanel, setIsDraggingPerformancePanel] = useState(false);
  const performancePanelRef = useRef<HTMLDivElement>(null);
  const performancePanelDragStart = useRef({ x: 0, y: 0 });
  const [repairSummary, setRepairSummary] = useState<string | null>(null);
  const [floorplannerCanvas, setFloorplannerCanvas] =
    useState<HTMLCanvasElement | null>(null);

  // Handle debug panel dragging
  useEffect(() => {
    if (!isDraggingDebugPanel) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartPos.current.x;
      const deltaY = e.clientY - dragStartPos.current.y;
      setDebugPanelPosition(prev => ({
        x: Math.max(0, Math.min(window.innerWidth - 300, prev.x + deltaX)),
        y: Math.max(0, Math.min(window.innerHeight - 400, prev.y + deltaY))
      }));
      dragStartPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      setIsDraggingDebugPanel(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingDebugPanel]);

  // Handle Connection Metadata panel dragging
  useEffect(() => {
    if (!isDraggingConnectionMetadata) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - connectionMetadataDragStart.current.x;
      const deltaY = e.clientY - connectionMetadataDragStart.current.y;
      setConnectionMetadataPosition(prev => ({
        x: Math.max(0, Math.min(window.innerWidth - CONNECTION_METADATA_PANEL_WIDTH, prev.x + deltaX)),
        y: Math.max(
          0,
          Math.min(
            window.innerHeight - CONNECTION_METADATA_PANEL_MIN_HEIGHT - DEBUG_PANEL_VIEWPORT_MARGIN,
            prev.y + deltaY
          )
        )
      }));
      connectionMetadataDragStart.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      setIsDraggingConnectionMetadata(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingConnectionMetadata]);

  // Handle Performance Metrics panel dragging
  useEffect(() => {
    if (!isDraggingPerformancePanel) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - performancePanelDragStart.current.x;
      const deltaY = e.clientY - performancePanelDragStart.current.y;
      setPerformancePanelPosition(prev => ({
        x: Math.max(0, Math.min(window.innerWidth - 320, prev.x + deltaX)),
        y: Math.max(0, Math.min(window.innerHeight - 200, prev.y + deltaY))
      }));
      performancePanelDragStart.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      setIsDraggingPerformancePanel(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingPerformancePanel]);

  useEffect(() => {
    if (!editorStore.debugPerformance) {
      return;
    }

    const updateSnapshot = () => {
      setPerformanceSnapshot(capturePerformanceSnapshot());
    };

    updateSnapshot();
    const interval = window.setInterval(updateSnapshot, 1500);
    return () => {
      window.clearInterval(interval);
    };
  }, [editorStore.debugPerformance]);

  const connectionMetadataRows = editorStore.debugConnectionMetadata
    ? [
        // Process walls
        ...Array.from(floorplannerStore.wallsMap.values()).map((wall) => {
          const legacyConnections = wall.centerConnections ?? [];
          const centerTargets = Array.from(
            new Set(
              legacyConnections
                .filter((connection) => connection.mode !== "edge")
                .map((connection) => connection.id ?? "")
                .filter(Boolean)
            )
          );
          const edgeTargets = Array.from(
            new Set([
              ...(wall.edgeConnections ?? []).map((connection) => connection.targetWallId ?? "").filter(Boolean),
              ...legacyConnections
                .filter((connection) => connection.mode === "edge")
                .map((connection) => connection.id ?? "")
                .filter(Boolean),
            ])
          );
          const attachments = Array.from(
            new Set((wall.symbolAttachments ?? []).map((attachment) => attachment.symbolId ?? "").filter(Boolean))
          );

          return {
            id: wall.id,
            label: wall.name ? `${wall.name} (${wall.id})` : `Wall: ${wall.id}`,
            type: 'wall',
            centerTargets,
            edgeTargets,
            attachments,
          };
        }),
        // Process single lines
        ...Array.from(floorplannerStore.singleLinesMap.values()).map((line) => {
          const centerTargets = Array.from(
            new Set(
              (line.centerConnections ?? [])
                .map((connection) => connection.id ?? "")
                .filter(Boolean)
            )
          );

          return {
            id: line.id,
            label: line.name ? `${line.name} (${line.id})` : `SingleLine: ${line.id}`,
            type: 'singleLine',
            centerTargets,
            edgeTargets: [], // Single lines don't have edge connections
            attachments: [], // Single lines don't have symbol attachments
          };
        }),
        // Process ruler lines
        ...Array.from(floorplannerStore.rulerLinesMap.values()).map((ruler) => {
          const centerTargets = Array.from(
            new Set(
              (ruler.centerConnections ?? [])
                .map((connection) => connection.id ?? "")
                .filter(Boolean)
            )
          );

          return {
            id: ruler.id,
            label: ruler.name ? `${ruler.name} (${ruler.id})` : `RulerLine: ${ruler.id}`,
            type: 'rulerLine',
            centerTargets,
            edgeTargets: [], // Ruler lines don't have edge connections
            attachments: [], // Ruler lines don't have symbol attachments
          };
        }),
      ].filter((row) => row.centerTargets.length > 0 || row.edgeTargets.length > 0 || row.attachments.length > 0)
    : [];

  const totalCenterConnections = connectionMetadataRows.reduce(
    (sum, row) => sum + row.centerTargets.length,
    0
  );
  const totalEdgeConnections = connectionMetadataRows.reduce((sum, row) => sum + row.edgeTargets.length, 0);
  const totalAttachments = connectionMetadataRows.reduce((sum, row) => sum + row.attachments.length, 0);
  const connectionMetadataPanelMaxHeight = Math.min(
    CONNECTION_METADATA_PANEL_MAX_HEIGHT,
    Math.max(
      CONNECTION_METADATA_PANEL_MIN_HEIGHT,
      window.innerHeight - connectionMetadataPosition.y - DEBUG_PANEL_VIEWPORT_MARGIN
    )
  );

  const formatConnectionList = (items: string[]) => {
    if (!items || items.length === 0) return "None";
    if (items.length > 3) {
      return `${items.slice(0, 3).join(", ")}, ...`;
    }
    return items.join(", ");
  };

  const handleDebugPanelMouseDown = (e: React.MouseEvent) => {
    // Don't start dragging if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.closest('button') || target.closest('input')) {
      return;
    }

    if (debugPanelRef.current) {
      setIsDraggingDebugPanel(true);
      dragStartPos.current = { x: e.clientX, y: e.clientY };
      e.preventDefault();
    }
  };

  const handleConnectionMetadataMouseDown = (e: React.MouseEvent) => {
    // Don't start dragging if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.closest('button') || target.closest('input')) {
      return;
    }

    if (connectionMetadataRef.current) {
      setIsDraggingConnectionMetadata(true);
      connectionMetadataDragStart.current = { x: e.clientX, y: e.clientY };
      e.preventDefault();
    }
  };

  const handlePerformancePanelMouseDown = (e: React.MouseEvent) => {
    // Don't start dragging if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.closest('button') || target.closest('input')) {
      return;
    }

    if (performancePanelRef.current) {
      setIsDraggingPerformancePanel(true);
      performancePanelDragStart.current = { x: e.clientX, y: e.clientY };
      e.preventDefault();
    }
  };

  const handleDragStart = useCallback(() => {
    // Do nothing
  }, []);

  const handleDragEnd = useCallback(() => {
    // Do nothing
  }, []);

  const handleDrag = useCallback(() => {
    // Do nothing
  }, []);

  const CanvasEventHandler: React.FC = () => {
    const { camera, gl, scene } = useThree();

    useEffect(() => {
      editorStore.setCamera(camera);
      setFloorplannerCanvas(gl.domElement);

      const handleWheel = (event: WheelEvent) => {
        editorStore.handleWheel(event);
      };

      const canvas = gl.domElement;
      canvas.addEventListener("wheel", handleWheel, { passive: false });

      return () => {
        canvas.removeEventListener("wheel", handleWheel);
      };
    }, [camera, editorStore, gl, scene]);

    return null;
  };

  // Auto-save periodically only when modified and not dragging
  useEffect(() => {
    const interval = setInterval(() => {
      if (!floorplannerStore.dirty) return;
      if (floorplannerStore.lockedByOther) return;
      if (renderStore.isDragging) return;
      if (floorplannerStore.blockDirty) return;
      // If persisted JSON equals current JSON, treat as clean
      if (floorplannerStore.lastSavedJson && floorplannerStore.lastSavedJson === floorplannerStore.toJSON()) {
        floorplannerStore.setDirty(false);
        return;
      }
      saveToCloud(false, false).catch((error) => {
        console.error("Auto-save failed:", error);
      });
    }, 3000);

    return () => clearInterval(interval); // Cleanup on unmount
  }, [saveToCloud]);

  // Detect backspace key to delete selected item
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Backspace") {
        if (document.activeElement === document.body && !editorStore.wallEditingOuterLength && !editorStore.wallEditingInnerLength) {
          editorStore.delete();
        }
      }

      // Dev-only: Debug mode keyboard shortcuts
      if (process.env.NODE_ENV === 'production') return;

      // Ctrl+Shift+D to toggle all debug visualizations
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        if (editorStore.isDebugModeActive) {
          editorStore.disableAllDebugVisualizations();
          console.log('Debug mode: All visualizations disabled');
        } else {
          editorStore.enableAllDebugVisualizations();
          console.log('Debug mode: All visualizations enabled');
        }
      }

      // Ctrl+Shift+E to toggle edge connections debug visualization
      if (event.ctrlKey && event.shiftKey && event.key === 'E') {
        event.preventDefault();
        editorStore.setDebugEdgeConnections(!editorStore.debugEdgeConnections);
        if (editorStore.debugEdgeConnections) {
          editorStore.enableAllDebugVisualizations();
          console.log('Edge connections debug enabled');
        } else {
          editorStore.disableAllDebugVisualizations();
          console.log('Edge connections debug disabled');
        }
      }

      // Ctrl+Shift+J to toggle joint polygon debug
      if (event.ctrlKey && event.shiftKey && event.key === 'J') {
        event.preventDefault();
        editorStore.setDebugJointPolygons(!editorStore.debugJointPolygons);
      }

      // Ctrl+Shift+O to toggle outline segment debug
      if (event.ctrlKey && event.shiftKey && event.key === 'O') {
        event.preventDefault();
        editorStore.setDebugOutlineSegments(!editorStore.debugOutlineSegments);
      }

      // Ctrl+Shift+L to toggle wall length debug
      if (event.ctrlKey && event.shiftKey && event.key === 'L') {
        event.preventDefault();
        editorStore.setDebugWallLengths(!editorStore.debugWallLengths);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [editorStore]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <Canvas
        id="floorplanner-canvas"
        camera={floorPlan2DCamera}
        frameloop="demand"
        dpr={Math.min(window.devicePixelRatio, 2)}
        shadows={show3DScene && !editorStore.hide3DShadows}
        style={{
          userSelect: "none",
          // View-only 2D B&W; CSS filter does not affect WebGL exports.
          filter:
            !show3DScene && editorStore.showBlackAndWhite
              ? "grayscale(100%)"
              : undefined,
        }}
        gl={{
          localClippingEnabled: true,
          antialias: true,
          toneMapping: THREE.NoToneMapping,
          preserveDrawingBuffer: true, // Required for saving images
          powerPreference: "high-performance", // Improve performance for large canvases
          depth: true,  // Ensure depth buffer is used
          alpha: true,  // Ensure transparency is allowed (optional, depending on your setup)
          stencil: true,  // Required for 3D selection outline masking
          logarithmicDepthBuffer: false,  // Disable logarithmic depth buffer unless you need it
        }}
      >
        <CameraSetup />
        <FloorPlan2DCamera camera={floorPlan2DCamera} active={!show3DScene} />
        <View3DSceneMountBridge
          show3DScene={show3DScene}
          setShow3DScene={setShow3DScene}
          setEntry2DView={setEntry2DView}
          onBeforeEnter3D={capture2DFrameForTransition}
        />
        {!show3DScene && <ambientLight intensity={1} />}
        {!show3DScene && <pointLight position={[10, 10, 10]} />}
        {!show3DScene && editorStore.showGrid && <Grid />}
        {!show3DScene && <PanningPlane />}
        {!show3DScene && <SelectionPlane />}
        {!show3DScene && <WallConstructionPlane />}
        {!show3DScene && <LineConstructionPlane />}
        {!show3DScene && <RulerConstructionPlane />}
        {!show3DScene && <TopDown2DCameraGuard restoredView={entry2DView} />}
        <CanvasEventHandler />
        {!show3DScene && <AreaConstructionPlane />}
        {!show3DScene && <AreaBleedFillPlane />}
        <FloorplannerStoreProvider>
          {show3DScene ? (
            <FloorPlan3DScene
              active={editorStore.view3D}
              entry2DView={entry2DView}
              onExit2DView={setEntry2DView}
              onExitComplete={() => {
                setShow3DScene(false);
                setThreeDBaseFrameReady(false);
                setShowThreeDMeasurements(false);
                reveal3DFrame();
              }}
              onModelLoadingChange={setThreeDModelsLoading}
              onFirstFrameReady={handleFirst3DFrameReady}
              renderMeasurements={showThreeDMeasurements}
            />
          ) : (
            <>
              {/* Render other symbols */}
              {Array.from(symbolsMap.values()).map((symbol) => (
                isDoorType(symbol) ? (
                  <Door key={symbol.id} door={symbol as DoorType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} attachedToWall={symbol.attachedTo ? floorplannerStore.wallsMap.get(symbol.attachedTo) : undefined} />
                ) : isSlidingDoorType(symbol) ? (
                  <SlidingDoor key={symbol.id} door={symbol as SlidingDoorType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} attachedToWall={symbol.attachedTo ? floorplannerStore.wallsMap.get(symbol.attachedTo) : undefined} />
                ) : isWindowType(symbol) ? (
                  <Window key={symbol.id} window={symbol as WindowType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} attachedToWall={symbol.attachedTo ? floorplannerStore.wallsMap.get(symbol.attachedTo) : undefined} />
                ) : isVoidType(symbol) ? (
                  <Void key={symbol.id} void={symbol as VoidType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} attachedToWall={symbol.attachedTo ? floorplannerStore.wallsMap.get(symbol.attachedTo) : undefined} />
                ) : isDoubleDoorType(symbol) ? (
                  <DoubleDoor key={symbol.id} doubleDoor={symbol as DoubleDoorType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} attachedToWall={symbol.attachedTo ? floorplannerStore.wallsMap.get(symbol.attachedTo) : undefined} />
                ) : isCircleStairsType(symbol) ? (
                  <CircleStairs key={symbol.id} circleStair={symbol as CircleStairsType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} />
                ) : isRectStairsType(symbol) ? (
                  <RectStairs key={symbol.id} rectStair={symbol as RectStairsType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} />
                ) : isTurnStairsType(symbol) ? (
                  <TurnStairs key={symbol.id} turnStair={symbol as TurnStairsType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} />
                ) : isSmartTableType(symbol) ? (
                  <SmartTableSymbol key={symbol.id} table={symbol as SmartTableType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} />
                ) : isBookShelfType(symbol) ? (
                  <BookShelfSymbol key={symbol.id} bookShelf={symbol as BookShelfType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} />
                ) : isSvgType(symbol) ? (
                  <SvgSymbol key={symbol.id} svg={symbol as SvgType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} />
                ) : isTextType(symbol) ? (
                  <TextSymbol key={symbol.id} text={symbol as TextType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} />
                ) : isSquareSymbolType(symbol) ? (
                  <SquareSymbol key={symbol.id} square={symbol as SquareSymbolType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} />
                ) : isCircleSymbolType(symbol) ? (
                  <CircleSymbol key={symbol.id} circle={symbol as CircleSymbolType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} />
                ) : isTriangleSymbolType(symbol) ? (
                  <TriangleSymbol key={symbol.id} triangle={symbol as TriangleSymbolType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} />
                ) : isPolygonSymbolType(symbol) ? (
                  <PolygonSymbol key={symbol.id} polygon={symbol as PolygonSymbolType} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} />
                ) : isAreaSymbolType(symbol) || isAreaFillSymbolType(symbol) ? (
                  <AreaSymbol key={symbol.id} symbol={symbol} onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} />
                ) : isBackdropImageType(symbol) ? (
                  <BackdropImage
                    key={symbol.id}
                    symbol={symbol as BackdropImageType}
                    imagePath={symbol.backdropImagePath}
                    position={new THREE.Vector2(symbol.position.x, symbol.position.y)}
                  />
                ) : (
                  null
                )
              ))}
              <WallStructure />
              <JointPolygonStructure />
              <WallOutlineStructure />
              <SingleLinesStructure />
              <RulerLines />
              <AlignmentLineStructure />
              <RoomLabelStructure />
              <DebugVisualization floorplannerStore={floorplannerStore} editorStore={editorStore} renderStore={renderStore} />
            </>
          )}
        </FloorplannerStoreProvider>
      </Canvas>
      <canvas
        ref={transitionCanvasRef}
        className={`pointer-events-none absolute inset-0 z-0 h-full w-full ${
          viewTransitionPhase === "holding"
            ? "opacity-100 transition-none"
            : viewTransitionPhase === "fading"
              ? "opacity-0 transition-opacity ease-out motion-reduce:transition-none"
              : "opacity-0 transition-none"
        }`}
        style={{ transitionDuration: `${VIEW_TRANSITION_FADE_MS}ms` }}
        aria-hidden="true"
        onTransitionEnd={(event) => {
          if (
            event.target === event.currentTarget &&
            event.propertyName === "opacity" &&
            viewTransitionPhase === "fading"
          ) {
            finishViewTransition();
          }
        }}
      />
      {show3DScene && (
        <div
          className="absolute right-4 top-4 z-20 flex items-center gap-2 rounded bg-gray-950/85 px-3 py-1.5 text-xs font-semibold tracking-wide text-white shadow"
          role={showThreeDModelsLoading ? "status" : undefined}
          aria-live={showThreeDModelsLoading ? "polite" : undefined}
        >
          {showThreeDModelsLoading && (
            <span
              className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/35 border-t-white"
              aria-hidden="true"
            />
          )}
          {showThreeDModelsLoading ? "Loading 3D objects" : "3D View"}
        </div>
      )}

      {/* Comprehensive Debug Panel (activated via ?debug=1 URL param) */}
      {editorStore.debugPanelVisible && (
        <>
          <div
            ref={debugPanelRef}
            className="absolute z-[9999] cursor-move"
            style={{
              left: `${debugPanelPosition.x}px`,
              top: `${debugPanelPosition.y}px`
            }}
          >
            <div className="bg-black/80 text-white p-3 rounded-md shadow-lg font-mono text-xs min-w-[280px]">
              <div
                className="flex items-center justify-between mb-2 pb-2 border-b border-gray-600 select-none"
                onMouseDown={handleDebugPanelMouseDown}
              >
                <span className="font-bold text-yellow-400">DEBUG MODE</span>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${editorStore.isDebugModeActive ? 'text-green-400' : 'text-gray-400'}`}>
                    {editorStore.isDebugModeActive ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                  <button
                    onClick={() => {
                      if (editorStore.isDebugModeActive) {
                        editorStore.disableAllDebugVisualizations();
                      } else {
                        editorStore.enableAllDebugVisualizations();
                      }
                    }}
                    className="px-2 py-1 bg-yellow-600 hover:bg-yellow-500 rounded text-black text-[10px] font-bold"
                  >
                    {editorStore.isDebugModeActive ? 'DISABLE ALL' : 'ENABLE ALL'}
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                <label className="flex items-center gap-2 cursor-pointer hover:bg-white/10 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={editorStore.debugEdgeConnections}
                    onChange={(e) => {
                      editorStore.setDebugEdgeConnections(e.target.checked);
                      if (e.target.checked) {
                        console.log('Edge connections debug enabled');
                      } else {
                        console.log('Edge connections debug disabled');
                      }
                    }}
                    className="form-checkbox"
                  />
                  <span>Edge Connections Debug (Ctrl+Shift+E)</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer hover:bg-white/10 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={editorStore.debugJointPolygons}
                    onChange={(e) => editorStore.setDebugJointPolygons(e.target.checked)}
                    className="form-checkbox"
                  />
                  <span>Joint Polygons (Ctrl+Shift+J)</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer hover:bg-white/10 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={editorStore.debugOutlineSegments}
                    onChange={(e) => editorStore.setDebugOutlineSegments(e.target.checked)}
                    className="form-checkbox"
                  />
                  <span>Outline Segments (Ctrl+Shift+O)</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer hover:bg-white/10 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={editorStore.debugImaginaryLines}
                    onChange={(e) => editorStore.setDebugImaginaryLines(e.target.checked)}
                    className="form-checkbox"
                  />
                  <span>Imaginary Lines</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer hover:bg-white/10 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={editorStore.debugImaginaryIntersections}
                    onChange={(e) => editorStore.setDebugImaginaryIntersections(e.target.checked)}
                    className="form-checkbox"
                  />
                  <span>Imaginary Intersection Points</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer hover:bg-white/10 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={editorStore.debugFillCorners}
                    onChange={(e) => editorStore.setDebugFillCorners(e.target.checked)}
                    className="form-checkbox"
                  />
                  <span>Fill Corner Markers</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer hover:bg-white/10 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={editorStore.debugWallLengths}
                    onChange={(e) => editorStore.setDebugWallLengths(e.target.checked)}
                    className="form-checkbox"
                  />
                  <span>Wall info (Ctrl+Shift+L)</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer hover:bg-white/10 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={editorStore.debugConnectionMetadata}
                    onChange={(e) => editorStore.setDebugConnectionMetadata(e.target.checked)}
                    className="form-checkbox"
                  />
                  <span>Connection Metadata</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer hover:bg-white/10 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={editorStore.debugPerformance}
                    onChange={(e) => editorStore.setDebugPerformance(e.target.checked)}
                    className="form-checkbox"
                  />
                  <span>Performance Metrics</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer hover:bg-white/10 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={editorStore.debugDisableWallGeometryCache}
                    onChange={(e) => {
                      editorStore.setDebugDisableWallGeometryCache(e.target.checked);
                      if (e.target.checked) {
                        console.log('WallGeometryCache disabled - all geometry will be recalculated');
                        // Clear the cache when disabling
                        wallGeometryCache.clearAll();
                      } else {
                        console.log('WallGeometryCache enabled');
                      }
                    }}
                    className="form-checkbox"
                  />
                  <span>Disable Wall Geometry Cache</span>
                </label>
              </div>

              <div className="mt-3 rounded border border-green-500/30 bg-green-500/10 p-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-green-200">
                      Floorplan Repair
                    </p>
                    <p className="text-[10px] text-gray-200">
                      Recenters drifted walls and clamps extreme scales to restore visibility.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      const result = floorplannerStore.repairFloorplanLayout();
                      if (result.applied) {
                        const shift = result.translation.length();
                        setRepairSummary(
                          `Shift ${shift.toFixed(2)}m · scale ×${result.scale.toFixed(2)} · ${result.normalizedObjects} objects`
                        );
                      } else if (result.reason === "EMPTY_SCENE") {
                        setRepairSummary("Nothing to repair – no drawables detected.");
                      } else {
                        setRepairSummary("Layout already centered and within sane bounds.");
                      }
                    }}
                    className="px-2 py-1 rounded bg-green-400/90 text-black text-[10px] font-bold hover:bg-green-300 transition-colors"
                  >
                    Repair Layout
                  </button>
                </div>
                {repairSummary && (
                  <div className="mt-2 text-[10px] text-gray-100">
                    {repairSummary}
                  </div>
                )}
              </div>

              <div className="mt-2 pt-2 border-t border-gray-600 text-[10px] text-gray-400">
                <div>Active flags: {[
                  editorStore.debugEdgeConnections && 'Edges',
                  editorStore.debugJointPolygons && 'Joints',
                  editorStore.debugOutlineSegments && 'Segments',
                  editorStore.debugWallLengths && 'Lengths',
                  editorStore.debugConnectionMetadata && 'Metadata',
                  editorStore.debugPerformance && 'Performance',
                  editorStore.debugFillCorners && 'FillCorners'
                ].filter(Boolean).length || 0}</div>
                <div>Mode: Edge-based</div>
              </div>
            </div>
          </div>

          {editorStore.debugConnectionMetadata && (
            <div
              ref={connectionMetadataRef}
              className="absolute z-[9999] cursor-move"
              style={{
                left: `${connectionMetadataPosition.x}px`,
                top: `${connectionMetadataPosition.y}px`,
                width: `${CONNECTION_METADATA_PANEL_WIDTH}px`
              }}
            >
              <div
                className="flex min-h-0 flex-col overflow-hidden rounded-md border border-white/20 bg-black/70 p-2 font-mono text-[11px] text-white shadow-lg"
                style={{ maxHeight: `${connectionMetadataPanelMaxHeight}px` }}
              >
                <div
                  className="mb-2 flex shrink-0 cursor-move select-none items-center justify-between"
                  onMouseDown={handleConnectionMetadataMouseDown}
                >
                  <span className="text-[12px] font-semibold text-yellow-300">Connection Metadata</span>
                  <span className="text-[10px] text-gray-300">
                    Edges: {totalEdgeConnections} · Center: {totalCenterConnections} · Attachments: {totalAttachments}
                  </span>
                </div>
                {connectionMetadataRows.length === 0 ? (
                  <div className="shrink-0 text-[10px] text-gray-400">No active connections detected.</div>
                ) : (
                  <div className="min-h-0 flex-1 space-y-1 overflow-y-auto overflow-x-hidden pr-1">
                    {connectionMetadataRows.map((row) => (
                      <div key={row.id} className="border-b border-white/10 pb-1 last:border-b-0 last:pb-0">
                        <div className="flex items-center justify-between text-[11px] font-semibold text-white">
                          <span>{row.label}</span>
                          <span className="text-[10px] text-green-300">
                            {(row.centerTargets.length + row.edgeTargets.length)} conn
                          </span>
                        </div>
                        <div className="text-[10px] text-gray-300">
                          <span className="font-semibold text-white">Center:</span> {formatConnectionList(row.centerTargets)}
                        </div>
                        <div className="text-[10px] text-gray-300">
                          <span className="font-semibold text-white">Edge:</span> {formatConnectionList(row.edgeTargets)}
                        </div>
                        {row.attachments.length > 0 && (
                          <div className="text-[10px] text-gray-300">
                            <span className="font-semibold text-white">Attachments:</span> {formatConnectionList(row.attachments)}
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="text-[10px] text-gray-400">
                      Showing {connectionMetadataRows.length} objects
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {editorStore.debugPerformance && (
            <div
              ref={performancePanelRef}
              className="absolute z-[9999] cursor-move"
              style={{
                left: `${performancePanelPosition.x}px`,
                top: `${performancePanelPosition.y}px`,
                width: '300px'
              }}
            >
              <div className="rounded-md border border-white/20 bg-gradient-to-br from-black/90 to-black/40 p-3 font-mono text-[11px] text-white shadow-lg backdrop-blur">
                <div
                  className="mb-1 flex items-center justify-between text-[12px] font-semibold text-lime-300 cursor-move select-none"
                  onMouseDown={handlePerformancePanelMouseDown}
                >
                  <span>Performance Metrics</span>
                  <span className="text-[10px] text-gray-300">Auto-refresh</span>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-[11px]">
                    <span>FPS</span>
                    <span>{performanceSnapshot.analysis?.timing?.averageFrameRate?.toFixed(1) ?? "N/A"}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span>Memory (MB)</span>
                    <span>{performanceSnapshot.analysis?.memory?.currentUsage?.toFixed(1) ?? "N/A"}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span>Overall Score</span>
                    <span>{performanceSnapshot.analysis?.userExperience?.overallScore?.toFixed(1) ?? "N/A"}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span>Active Drag Ops</span>
                    <span>{performanceSnapshot.dragMetrics?.activeOperations ?? 0}</span>
                  </div>
                  {performanceSnapshot.bottlenecks.length > 0 && (
                    <div className="rounded border border-white/10 bg-white/5 p-2 text-[10px] text-yellow-200">
                      <div className="font-semibold text-[10.5px] text-yellow-100">Bottlenecks</div>
                      <div className="text-[10px] text-yellow-200">
                        {performanceSnapshot.bottlenecks.slice(0, 3).map((bottleneck, idx) => (
                          <div key={`bottleneck-${idx}`} className="truncate">
                            • {bottleneck}
                          </div>
                        ))}
                        {performanceSnapshot.bottlenecks.length > 3 && (
                          <div>…and {performanceSnapshot.bottlenecks.length - 3} more</div>
                        )}
                      </div>
                    </div>
                  )}
                  {performanceSnapshot.alerts.length > 0 && (
                    <div className="rounded border border-red-500/40 bg-red-500/10 p-2 text-[10px] text-red-200">
                      <div className="font-semibold text-[10.5px] text-red-100">Alerts</div>
                      {performanceSnapshot.alerts.slice(0, 3).map((alert) => (
                        <div key={alert.id} className="truncate">
                          [{alert.severity.toUpperCase()}] {alert.message}
                        </div>
                      ))}
                      {performanceSnapshot.alerts.length > 3 && (
                        <div>...and {performanceSnapshot.alerts.length - 3} more</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
});

export default FloorPlan;
